import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import bookingService from "../../services/bookingService";
import logger from "../../utils/logger";

export const UPCOMING_STATUSES = [
  "pending",
  "pending_reschedule_confirmation",
  "provider_cancelled",
  "assigned",
];
export const PAST_STATUSES = ["completed", "cancelled"];

// Fetch all bookings for the user
export const fetchBookings = createAsyncThunk(
  "bookings/fetchBookings",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await bookingService.getBookings(params);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch bookings";
      logger.error("Error fetching bookings:", error);
      return rejectWithValue(message);
    }
  }
);

// Fetch single booking by ID
export const fetchBookingById = createAsyncThunk(
  "bookings/fetchBookingById",
  async (bookingId, { rejectWithValue }) => {
    try {
      const data = await bookingService.getBookingById(bookingId);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch booking";
      logger.error("Error fetching booking:", error);
      return rejectWithValue(message);
    }
  }
);

// Cancel a booking
export const cancelBooking = createAsyncThunk(
  "bookings/cancelBooking",
  async ({ bookingId, reason, details }, { rejectWithValue }) => {
    try {
      const data = await bookingService.cancelBooking(bookingId, {
        reason,
        details,
      });
      return { bookingId, data };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to cancel booking";
      logger.error("Error cancelling booking:", error);
      return rejectWithValue(message);
    }
  }
);

// Reschedule a booking
export const rescheduleBooking = createAsyncThunk(
  "bookings/rescheduleBooking",
  async ({ bookingId, newRequestedDateTime }, { rejectWithValue }) => {
    try {
      const data = await bookingService.rescheduleBooking(bookingId, {
        newRequestedDateTime,
      });
      return { bookingId, data };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to reschedule booking";
      logger.error("Error rescheduling booking:", error);
      return rejectWithValue(message);
    }
  }
);

// Submit a review
export const submitReview = createAsyncThunk(
  "bookings/submitReview",
  async ({ bookingId, rating, comment }, { rejectWithValue }) => {
    try {
      const data = await bookingService.submitReview(bookingId, {
        rating,
        comment,
      });
      return { bookingId, data };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to submit review";
      logger.error("Error submitting review:", error);
      return rejectWithValue(message);
    }
  }
);

// Fetch reviews for a booking
export const fetchBookingReviews = createAsyncThunk(
  "bookings/fetchBookingReviews",
  async (bookingId, { rejectWithValue }) => {
    try {
      const data = await bookingService.getBookingReviews(bookingId);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch reviews";
      logger.error("Error fetching booking reviews:", error);
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  // Bookings list
  bookings: [],
  bookingsLoading: false,
  bookingsError: null,

  // Single booking detail
  currentBooking: null,
  currentBookingLoading: false,
  currentBookingError: null,

  // Reviews for current booking
  currentBookingReviews: null,
  reviewsLoading: false,
  reviewsError: null,

  // Filters
  statusFilter: "all", // 'all', 'upcoming', 'past'

  // Pagination
  page: 1,
  limit: 10,
  totalPages: 1,
  totalResults: 0,
};

const bookingsSlice = createSlice({
  name: "bookings",
  initialState,
  reducers: {
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
      state.page = 1; // Reset to first page when filter changes
    },

    clearCurrentBooking: (state) => {
      state.currentBooking = null;
      state.currentBookingError = null;
    },

    clearBookingsError: (state) => {
      state.bookingsError = null;
    },

    clearCurrentBookingError: (state) => {
      state.currentBookingError = null;
    },

    clearBookingReviews: (state) => {
      state.currentBookingReviews = null;
      state.reviewsError = null;
    },
  },

  extraReducers: (builder) => {
    // Fetch bookings
    builder
      .addCase(fetchBookings.pending, (state) => {
        state.bookingsLoading = true;
        state.bookingsError = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.bookingsLoading = false;

        // Handle paginated response from the API
        if (action.payload && Array.isArray(action.payload.bookings)) {
          state.bookings = action.payload.bookings;
          state.page = action.payload.page;
          state.limit = action.payload.limit;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        } else if (Array.isArray(action.payload)) {
          // Handle simple array response
          state.bookings = action.payload;
          state.page = 1;
          state.totalPages = 1;
          state.totalResults = action.payload.length;
        } else {
          // Handle empty or invalid response
          state.bookings = [];
          state.page = 1;
          state.totalPages = 1;
          state.totalResults = 0;
        }
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.bookingsLoading = false;
        state.bookingsError = action.payload;
      });

    // Fetch single booking
    builder
      .addCase(fetchBookingById.pending, (state) => {
        state.currentBookingLoading = true;
        state.currentBookingError = null;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.currentBookingLoading = false;
        state.currentBooking = action.payload;
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.currentBookingLoading = false;
        state.currentBookingError = action.payload;
      });

    // Cancel booking
    // We need separate error and loading states for
    // cancelling from currentBooking loading and error
    builder
      .addCase(cancelBooking.pending, (state) => {
        // state.currentBookingLoading = true;
      })
      .addCase(cancelBooking.fulfilled, (state, action) => {
        // state.currentBookingLoading = false;
        // Update booking in list
        // const index = state.bookings.findIndex(
        //   (b) => b.id === action.payload.bookingId
        // );
        // if (index !== -1) {
        //   state.bookings[index] = action.payload.data;
        // }
        // Update current booking if it's the one being viewed
        // if (state.currentBooking?.id === action.payload.bookingId) {
        //   state.currentBooking = action.payload.data;
        // }
      })
      .addCase(cancelBooking.rejected, (state, action) => {
        // state.currentBookingLoading = false;
        // state.currentBookingError = action.payload;
      });

    // Reschedule booking
    // We need separate error and loading states for
    // rescheduling from currentBooking loading and error
    builder
      .addCase(rescheduleBooking.pending, (state) => {
        // state.currentBookingLoading = true;
      })
      .addCase(rescheduleBooking.fulfilled, (state, action) => {
        // state.currentBookingLoading = false;
        // Update booking in list
        // const index = state.bookings.findIndex(
        //   (b) => b.id === action.payload.bookingId
        // );
        // if (index !== -1) {
        //   state.bookings[index] = action.payload.data;
        // }
        // Update current booking if it's the one being viewed
        // if (state.currentBooking?.id === action.payload.bookingId) {
        //   state.currentBooking = action.payload.data;
        // }
      })
      .addCase(rescheduleBooking.rejected, (state, action) => {
        // state.currentBookingLoading = false;
        // state.currentBookingError = action.payload;
      });

    // Submit review
    builder
      .addCase(submitReview.pending, (state) => {
        state.currentBookingLoading = true;
      })
      .addCase(submitReview.fulfilled, (state, action) => {
        state.currentBookingLoading = false;

        // Update booking in list
        const index = state.bookings.findIndex(
          (b) => b.id === action.payload.bookingId
        );
        if (index !== -1) {
          state.bookings[index] = action.payload.data;
        }

        // Update current booking
        if (state.currentBooking?.id === action.payload.bookingId) {
          state.currentBooking = action.payload.data;
        }
      })
      .addCase(submitReview.rejected, (state, action) => {
        state.currentBookingLoading = false;
        state.currentBookingError = action.payload;
      });

    // Fetch booking reviews
    builder
      .addCase(fetchBookingReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsError = null;
      })
      .addCase(fetchBookingReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        state.currentBookingReviews = action.payload;
      })
      .addCase(fetchBookingReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsError = action.payload;
      });
  },
});

export const {
  setStatusFilter,
  clearCurrentBooking,
  clearBookingsError,
  clearCurrentBookingError,
  clearBookingReviews,
} = bookingsSlice.actions;

// Selectors
export const selectBookings = (state) => state.bookings.bookings;
export const selectBookingsLoading = (state) => state.bookings.bookingsLoading;
export const selectBookingsError = (state) => state.bookings.bookingsError;

export const selectCurrentBooking = (state) => state.bookings.currentBooking;
export const selectCurrentBookingLoading = (state) =>
  state.bookings.currentBookingLoading;
export const selectCurrentBookingError = (state) =>
  state.bookings.currentBookingError;

export const selectStatusFilter = (state) => state.bookings.statusFilter;

// Single source of truth for bookings array
export const selectAllBookings = (state) => state.bookings.bookings || [];

// Filtered bookings based on status (simplified to 3 categories)
export const selectFilteredBookings = (state) => {
  const allBookings = selectAllBookings(state);
  const { statusFilter } = state.bookings;

  if (statusFilter === "all") return allBookings;

  if (statusFilter === "upcoming") {
    // Upcoming: pending, confirmed, in_progress
    return allBookings.filter((b) => UPCOMING_STATUSES.includes(b.status));
  }

  if (statusFilter === "past") {
    // Past: completed and cancelled
    return allBookings.filter((b) => PAST_STATUSES.includes(b.status));
  }

  return allBookings;
};

export const selectCurrentBookingReviews = (state) =>
  state.bookings.currentBookingReviews;
export const selectReviewsLoading = (state) => state.bookings.reviewsLoading;
export const selectReviewsError = (state) => state.bookings.reviewsError;
export const selectHomeownerReview = (state) =>
  state.bookings.currentBookingReviews?.homeownerReview || null;

export default bookingsSlice.reducer;
